import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, MessageCircle, Satellite } from "lucide-react";
import { openWhatsApp } from "../../lib/whatsapp";
import { useT } from "../../lib/i18n";
import { LanguageToggle } from "./LanguageToggle";

const linksData = [
    { to: "/", en: "Home", te: "హోమ్" },
    { to: "/about", en: "About", te: "మా గురించి" },
    { to: "/services", en: "Services", te: "సేవలు" },
    { to: "/retailers", en: "For Retailers", te: "రిటైలర్ల కోసం" },
    { to: "/coverage", en: "Coverage Area", te: "సేవా ప్రాంతం" },
    { to: "/contact", en: "Contact", te: "సంప్రదించండి" },
];

export const Navbar = () => {
    const [open, setOpen] = useState(false);
    const location = useLocation();
    const t = useT();
    const links = linksData.map((l) => ({ to: l.to, label: t(l.en, l.te) }));

    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    useEffect(() => {
        document.body.style.overflow = open ? "hidden" : "";
    }, [open]);

    return (
        <header className="sticky top-0 z-40 glass-header" data-testid="site-navbar">
            <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 md:h-20 flex items-center justify-between">
                <Link to="/" className="flex items-center gap-2.5" data-testid="nav-logo">
                    <span className="w-9 h-9 rounded-lg bg-rvs-orange flex items-center justify-center">
                        <Satellite className="w-5 h-5 text-white" strokeWidth={2.2} />
                    </span>
                    <span className="flex flex-col leading-none">
                        <span style={{fontFamily: "'Outfit', sans-serif"}} className="font-extrabold tracking-tight text-rvs-navy text-lg">
                            RVS Electronics
                        </span>
                        <span className="text-[10px] uppercase tracking-[0.2em] text-rvs-orange font-bold mt-0.5">
                            {t("Sun Direct Distributor", "సన్ డైరెక్ట్ డిస్ట్రిబ్యూటర్")}
                        </span>
                    </span>
                </Link>

                <nav className="hidden lg:flex items-center gap-9">
                    {links.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            data-testid={`nav-link-${l.to.replace("/", "") || "home"}`}
                            className={({ isActive }) =>
                                `text-sm font-medium link-underline transition-colors ${
                                    isActive ? "text-rvs-orange" : "text-rvs-navy hover:text-rvs-orange"
                                }`
                            }
                            end={l.to === "/"}
                        >
                            {l.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="hidden lg:flex items-center gap-3">
                    <LanguageToggle />
                    <button
                        onClick={() => openWhatsApp(t("Hello RVS Electronics, I'd like to talk.", "హలో RVS ఎలక్ట్రానిక్స్, నేను మాట్లాడాలనుకుంటున్నాను."))}
                        data-testid="nav-whatsapp-btn"
                        className="btn-orange px-5 py-2.5 rounded-full text-sm font-semibold inline-flex items-center gap-2"
                    >
                        <MessageCircle className="w-4 h-4" /> {t("Let's Talk", "మాట్లాడుదాం")}
                    </button>
                </div>

                <button
                    aria-label="Toggle menu"
                    className="lg:hidden text-rvs-navy"
                    onClick={() => setOpen((o) => !o)}
                    data-testid="nav-mobile-toggle"
                >
                    {open ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
                </button>
            </div>

            {open && (
                <div className="lg:hidden fixed top-16 left-0 right-0 bg-[#0B192C] text-white z-40 flex flex-col" style={{ backgroundColor: "#0B192C", height: "calc(100vh - 4rem)" }} data-testid="mobile-menu">
                    <nav className="flex-1 flex flex-col gap-2 px-6 py-10">
                        {links.map((l, i) => (
                            <NavLink
                                key={l.to}
                                to={l.to}
                                end={l.to === "/"}
                                data-testid={`mobile-nav-link-${l.to.replace("/", "") || "home"}`}
                                className={({ isActive }) =>
                                    `text-3xl font-bold tracking-tight py-3 border-b border-white/10 fade-up ${
                                        isActive ? "text-[#FF5A00]" : "text-white"
                                    }`
                                }
                                style={{ animationDelay: `${i * 60}ms`, fontFamily: "'Outfit', 'Noto Sans Telugu', sans-serif" }}
                            >
                                {l.label}
                            </NavLink>
                        ))}
                    </nav>
                    <div className="px-6 pb-10 space-y-4">
                        <div className="flex justify-center">
                            <LanguageToggle />
                        </div>
                        <button
                            onClick={() => openWhatsApp(t("Hello RVS Electronics, I'd like to talk.", "హలో RVS ఎలక్ట్రానిక్స్, నేను మాట్లాడాలనుకుంటున్నాను."))}
                            data-testid="mobile-whatsapp-btn"
                            className="btn-orange w-full py-4 rounded-full text-base font-semibold inline-flex items-center justify-center gap-2"
                        >
                            <MessageCircle className="w-5 h-5" /> {t("Chat on WhatsApp", "వాట్సాప్‌లో చాట్ చేయండి")}
                        </button>
                    </div>
                </div>
            )}
        </header>
    );
};
